import { supabase } from '../lib/supabase';

// Calculate dynamic date ranges based on period
const calculateDateRange = (period, customDateFrom, customDateTo) => {
  const now = new Date();
  
  if (period === 'day') {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return {
      fecha_inicio: today?.toISOString()?.split('T')?.[0],
      fecha_fin: today?.toISOString()?.split('T')?.[0]
    };
  }
  
  if (period === 'week') {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayOfWeek = today?.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday?.setDate(today?.getDate() + diffToMonday);
    const sunday = new Date(monday);
    sunday?.setDate(monday?.getDate() + 6);
    
    return {
      fecha_inicio: monday?.toISOString()?.split('T')?.[0],
      fecha_fin: sunday?.toISOString()?.split('T')?.[0]
    };
  }
  
  if (period === 'month') {
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return {
      fecha_inicio: firstDay?.toISOString()?.split('T')?.[0],
      fecha_fin: lastDay?.toISOString()?.split('T')?.[0]
    };
  }
  
  if (period === 'quarter') {
    const currentQuarter = Math.floor(now?.getMonth() / 3);
    const firstDay = new Date(now.getFullYear(), currentQuarter * 3, 1);
    const lastDay = new Date(now.getFullYear(), (currentQuarter + 1) * 3, 0);
    
    return {
      fecha_inicio: firstDay?.toISOString()?.split('T')?.[0],
      fecha_fin: lastDay?.toISOString()?.split('T')?.[0]
    };
  }
  
  if (period === 'year') {
    const firstDay = new Date(now.getFullYear(), 0, 1);
    const lastDay = new Date(now.getFullYear(), 11, 31);
    
    return {
      fecha_inicio: firstDay?.toISOString()?.split('T')?.[0],
      fecha_fin: lastDay?.toISOString()?.split('T')?.[0]
    };
  }
  
  if (period === 'custom' && customDateFrom && customDateTo) {
    return {
      fecha_inicio: customDateFrom,
      fecha_fin: customDateTo
    };
  }
  
  // Default to all time
  return {
    fecha_inicio: '1900-01-01',
    fecha_fin: now?.toISOString()?.split('T')?.[0]
  };
};

export const financialAnalysisService = {
  // Get global KPIs with filters
  async getGlobalKPIs(filters = {}) {
    try {
      const { fecha_inicio, fecha_fin } = calculateDateRange(
        filters?.period || 'all',
        filters?.dateFrom,
        filters?.dateTo
      );

      // Query the rentabilidad view with filters
      let query = supabase
        ?.from('rentabilidad_por_cliente_final')
        ?.select('*')
        ?.gte('fecha', fecha_inicio)
        ?.lte('fecha', fecha_fin);

      const { data, error } = await query;
      
      if (error) throw error;

      if (!data || data?.length === 0) {
        return {
          ingresosTotal: 0,
          costesTotal: 0,
          beneficioNeto: 0,
          margen: 0,
          numeroLoncheados: 0,
          ingresoMedio: 0
        };
      }

      // Apply additional filters
      let filteredData = data;

      if (filters?.clienteId && filters?.clienteId !== 'all') {
        filteredData = filteredData?.filter(d => d?.cliente_id === filters?.clienteId);
      }

      if (filters?.categoria && filters?.categoria !== 'all') {
        filteredData = filteredData?.filter(d => d?.categoria === filters?.categoria);
      }

      // For employee filter, we need to join with loncheados table
      if (filters?.empleadoId && filters?.empleadoId !== 'all') {
        // Get loncheados for this employee
        const { data: loncheadosData, error: loncheadosError } = await supabase
          ?.from('loncheados')
          ?.select('cliente_id, created_at')
          ?.eq('empleado_id', filters?.empleadoId)
          ?.gte('created_at', fecha_inicio)
          ?.lte('created_at', fecha_fin);

        if (loncheadosError) throw loncheadosError;

        // Filter by matching client and date
        const employeeClientDates = new Set(
          loncheadosData?.map(l => `${l?.cliente_id}_${l?.created_at?.split('T')?.[0]}`)
        );

        filteredData = filteredData?.filter(d => 
          employeeClientDates?.has(`${d?.cliente_id}_${d?.fecha}`)
        );
      }

      // Calculate aggregated KPIs
      const ingresosTotal = filteredData?.reduce((sum, d) => sum + (parseFloat(d?.facturacion_total) || 0), 0);
      const costesTotal = filteredData?.reduce((sum, d) => sum + (parseFloat(d?.coste_salarial_total) || 0), 0);
      const beneficioNeto = ingresosTotal - costesTotal;
      const margen = ingresosTotal > 0 ? (beneficioNeto / ingresosTotal) * 100 : 0;
      const numeroLoncheados = filteredData?.reduce((sum, d) => sum + (parseInt(d?.numero_loncheados) || 0), 0);
      const ingresoMedio = numeroLoncheados > 0 ? ingresosTotal / numeroLoncheados : 0;

      return {
        ingresosTotal,
        costesTotal,
        beneficioNeto,
        margen,
        numeroLoncheados,
        ingresoMedio
      };
    } catch (err) {
      console.error('Error fetching global KPIs:', err);
      throw err;
    }
  },

  // Get temporal evolution data for charts
  async getTemporalEvolution(filters = {}) {
    try {
      const { fecha_inicio, fecha_fin } = calculateDateRange(
        filters?.period || 'all',
        filters?.dateFrom,
        filters?.dateTo
      );

      let query = supabase
        ?.from('rentabilidad_por_cliente_final')
        ?.select('*')
        ?.gte('fecha', fecha_inicio)
        ?.lte('fecha', fecha_fin)
        ?.order('fecha', { ascending: true });

      const { data, error } = await query;
      
      if (error) throw error;

      if (!data || data?.length === 0) {
        return [];
      }

      // Apply filters
      let filteredData = data;

      if (filters?.clienteId && filters?.clienteId !== 'all') {
        filteredData = filteredData?.filter(d => d?.cliente_id === filters?.clienteId);
      }

      if (filters?.categoria && filters?.categoria !== 'all') {
        filteredData = filteredData?.filter(d => d?.categoria === filters?.categoria);
      }

      // Group by date
      const grouped = {};
      filteredData?.forEach(row => {
        const fecha = row?.fecha;
        
        if (!grouped?.[fecha]) {
          grouped[fecha] = {
            fecha,
            ingresos: 0,
            costes: 0,
            beneficio: 0
          };
        }

        grouped[fecha].ingresos += parseFloat(row?.facturacion_total) || 0;
        grouped[fecha].costes += parseFloat(row?.coste_salarial_total) || 0;
        grouped[fecha].beneficio += parseFloat(row?.beneficio_neto) || 0;
      });

      return Object.values(grouped);
    } catch (err) {
      console.error('Error fetching temporal evolution:', err);
      return [];
    }
  },

  // Get comparative data (ingresos vs costes by client)
  async getComparativeData(filters = {}) {
    try {
      const { fecha_inicio, fecha_fin } = calculateDateRange(
        filters?.period || 'all',
        filters?.dateFrom,
        filters?.dateTo
      );

      let query = supabase
        ?.from('rentabilidad_por_cliente_final')
        ?.select('*')
        ?.gte('fecha', fecha_inicio)
        ?.lte('fecha', fecha_fin);

      const { data, error } = await query;
      
      if (error) throw error;

      if (!data || data?.length === 0) {
        return [];
      }

      // Apply filters
      let filteredData = data;

      if (filters?.clienteId && filters?.clienteId !== 'all') {
        filteredData = filteredData?.filter(d => d?.cliente_id === filters?.clienteId);
      }

      if (filters?.categoria && filters?.categoria !== 'all') {
        filteredData = filteredData?.filter(d => d?.categoria === filters?.categoria);
      }

      // Group by client
      const clientMap = {};
      filteredData?.forEach(row => {
        const clienteId = row?.cliente_id;
        
        if (!clientMap?.[clienteId]) {
          clientMap[clienteId] = {
            name: row?.cliente_nombre,
            clienteNombre: row?.cliente_nombre,
            ingresos: 0,
            costes: 0,
            beneficio: 0
          };
        }

        clientMap[clienteId].ingresos += parseFloat(row?.facturacion_total) || 0;
        clientMap[clienteId].costes += parseFloat(row?.coste_salarial_total) || 0;
        clientMap[clienteId].beneficio += parseFloat(row?.beneficio_neto) || 0;
      });

      // Convert to array and sort by ingresos
      return Object.values(clientMap)
        ?.sort((a, b) => b?.ingresos - a?.ingresos)
        ?.slice(0, 10); // Top 10
    } catch (err) {
      console.error('Error fetching comparative data:', err);
      return [];
    }
  },

  // Get margin analysis by client and category
  async getMarginAnalysis(filters = {}) {
    try {
      const { fecha_inicio, fecha_fin } = calculateDateRange(
        filters?.period || 'all',
        filters?.dateFrom,
        filters?.dateTo
      );

      let query = supabase
        ?.from('rentabilidad_por_cliente_final')
        ?.select('*')
        ?.gte('fecha', fecha_inicio)
        ?.lte('fecha', fecha_fin);

      const { data, error } = await query;
      
      if (error) throw error;

      if (!data || data?.length === 0) {
        return { byClient: [], byCategory: [] };
      }

      // Apply filters
      let filteredData = data;

      if (filters?.clienteId && filters?.clienteId !== 'all') {
        filteredData = filteredData?.filter(d => d?.cliente_id === filters?.clienteId);
      }

      if (filters?.categoria && filters?.categoria !== 'all') {
        filteredData = filteredData?.filter(d => d?.categoria === filters?.categoria);
      }

      // Group by client
      const clientMap = {};
      filteredData?.forEach(row => {
        const clienteId = row?.cliente_id;
        
        if (!clientMap?.[clienteId]) {
          clientMap[clienteId] = {
            nombre: row?.cliente_nombre,
            ingresos: 0,
            costes: 0,
            beneficio: 0
          };
        }

        clientMap[clienteId].ingresos += parseFloat(row?.facturacion_total) || 0;
        clientMap[clienteId].costes += parseFloat(row?.coste_salarial_total) || 0;
        clientMap[clienteId].beneficio += parseFloat(row?.beneficio_neto) || 0;
      });

      const byClient = Object.values(clientMap)
        ?.map(c => ({
          name: c?.nombre,
          nombre: c?.nombre,
          margen: c?.ingresos > 0 ? (c?.beneficio / c?.ingresos) * 100 : 0
        }))
        ?.sort((a, b) => b?.margen - a?.margen)
        ?.slice(0, 10);

      // Group by category
      const categoryMap = {};
      filteredData?.forEach(row => {
        const categoria = row?.categoria;
        
        if (!categoryMap?.[categoria]) {
          categoryMap[categoria] = {
            categoria,
            ingresos: 0,
            costes: 0,
            beneficio: 0
          };
        }

        categoryMap[categoria].ingresos += parseFloat(row?.facturacion_total) || 0;
        categoryMap[categoria].costes += parseFloat(row?.coste_salarial_total) || 0;
        categoryMap[categoria].beneficio += parseFloat(row?.beneficio_neto) || 0;
      });

      const byCategory = Object.values(categoryMap)
        ?.map(c => ({
          categoria: c?.categoria,
          margen: c?.ingresos > 0 ? (c?.beneficio / c?.ingresos) * 100 : 0
        }))
        ?.sort((a, b) => b?.margen - a?.margen);

      return { byClient, byCategory };
    } catch (err) {
      console.error('Error fetching margin analysis:', err);
      return { byClient: [], byCategory: [] };
    }
  },

  // Get income distribution by client
  async getIncomeDistribution(filters = {}) {
    try {
      const { fecha_inicio, fecha_fin } = calculateDateRange(
        filters?.period || 'all',
        filters?.dateFrom,
        filters?.dateTo
      );

      let query = supabase
        ?.from('rentabilidad_por_cliente_final')
        ?.select('*')
        ?.gte('fecha', fecha_inicio)
        ?.lte('fecha', fecha_fin);

      const { data, error } = await query;
      
      if (error) throw error;

      if (!data || data?.length === 0) {
        return [];
      }

      // Apply filters
      let filteredData = data;

      if (filters?.clienteId && filters?.clienteId !== 'all') {
        filteredData = filteredData?.filter(d => d?.cliente_id === filters?.clienteId);
      }

      if (filters?.categoria && filters?.categoria !== 'all') {
        filteredData = filteredData?.filter(d => d?.categoria === filters?.categoria);
      }

      // Group by client
      const clientMap = {};
      filteredData?.forEach(row => {
        const clienteId = row?.cliente_id;
        
        if (!clientMap?.[clienteId]) {
          clientMap[clienteId] = {
            nombre: row?.cliente_nombre,
            ingresos: 0
          };
        }

        clientMap[clienteId].ingresos += parseFloat(row?.facturacion_total) || 0;
      });

      // Convert to array and sort
      return Object.values(clientMap)
        ?.sort((a, b) => b?.ingresos - a?.ingresos)
        ?.slice(0, 8); // Top 8 for pie chart
    } catch (err) {
      console.error('Error fetching income distribution:', err);
      return [];
    }
  },

  // Get detailed table data
  async getDetailedTableData(filters = {}) {
    try {
      const { fecha_inicio, fecha_fin } = calculateDateRange(
        filters?.period || 'all',
        filters?.dateFrom,
        filters?.dateTo
      );

      let query = supabase
        ?.from('rentabilidad_por_cliente_final')
        ?.select('*')
        ?.gte('fecha', fecha_inicio)
        ?.lte('fecha', fecha_fin);

      const { data, error } = await query;
      
      if (error) throw error;

      if (!data || data?.length === 0) {
        return [];
      }

      // Apply filters
      let filteredData = data;

      if (filters?.clienteId && filters?.clienteId !== 'all') {
        filteredData = filteredData?.filter(d => d?.cliente_id === filters?.clienteId);
      }

      if (filters?.categoria && filters?.categoria !== 'all') {
        filteredData = filteredData?.filter(d => d?.categoria === filters?.categoria);
      }

      // Group by client
      const clientMap = {};
      filteredData?.forEach(row => {
        const clienteId = row?.cliente_id;
        
        if (!clientMap?.[clienteId]) {
          clientMap[clienteId] = {
            clienteId,
            clienteNombre: row?.cliente_nombre,
            categoria: row?.categoria,
            ingresos: 0,
            costes: 0,
            beneficio: 0,
            numeroLoncheados: 0
          };
        }

        clientMap[clienteId].ingresos += parseFloat(row?.facturacion_total) || 0;
        clientMap[clienteId].costes += parseFloat(row?.coste_salarial_total) || 0;
        clientMap[clienteId].beneficio += parseFloat(row?.beneficio_neto) || 0;
        clientMap[clienteId].numeroLoncheados += parseInt(row?.numero_loncheados) || 0;
      });

      // Convert to array and calculate margin
      return Object.values(clientMap)?.map(client => ({
        ...client,
        margen: client?.ingresos > 0 ? (client?.beneficio / client?.ingresos) * 100 : 0
      }));
    } catch (err) {
      console.error('Error fetching detailed table data:', err);
      return [];
    }
  },

  // Get all clients for filter dropdown
  async getAllClients() {
    try {
      const { data, error } = await supabase
        ?.from('clientes')
        ?.select('id, nombre, categoria')
        ?.eq('activo', true)
        ?.order('nombre');

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching clients:', err);
      return [];
    }
  },

  // Get all employees for filter dropdown
  async getAllEmployees() {
    try {
      const { data, error } = await supabase
        ?.from('empleados')
        ?.select('id, nombre')
        ?.eq('activo', true)
        ?.order('nombre');

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching employees:', err);
      return [];
    }
  }
};
